import { useApolloClient, useLazyQuery, useMutation } from '@apollo/client';
import { formatTime, utils } from '@seedplanner/shared-utils';
import dayjs from 'dayjs';
import timezonePlugin from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { useUserContext } from '../../context/UserContext';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Typography,
} from '../../lib/base';
import { Help } from '../../lib/help';
import { AddCircleOutlineIcon } from '../../lib/icons';
import { DASHBOARD_OVERDUE_TASKS_QUERY, DASHBOARD_QUERY } from '../../queries/DashboardQueries';
import { SURPLUSES_BUFFERS_QUERY } from '../../queries/SurplusesQueries';
import type {
  EditRecurringOrderInputsDataQuery,
  EditRecurringOrderInputsDataQueryVariables,
  EditRecurringOrderInputsMutation,
  EditRecurringOrderInputsMutationVariables,
} from '../../types/__generated__/graphql';
import { createID, getTimeRanges } from '../../utils';
import { evictCacheData } from '../../utils/evictCacheData';
import { ExitDialogButton } from '../common/button';
import { ErrorView } from '../common/error';
import { PageLoading } from '../common/loading';
import NewCropOrBlendToUseForm, {
  type Props as NewCropOrBlendToUseFormProps,
  type CropData,
  type BlendData,
  type CropOrBlendToUseFormInput,
} from '../order/NewCropOrBlendToUseForm';
import { getOrderChanges } from '../order/getOrderChanges';
import { getTraysFromCropsAndBlends } from '../order/getTrayCounts';
import {
  filterParseSortBlendData,
  filterParseSortCropData,
  inputsToNewCropOrBlendToUseFormProps,
  preprareFormsForSubmit,
} from '../order/orderFormUtils';
import { createSizeKey } from '../order/sizeUtils';
import { ChildOrdersContainer, ChildOrdersContainerWithChanges } from './ChildOrdersContainer';
import { getLongestGrowTimePlusBufferFromInputForms } from './orderUtils';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

const EDIT_REC_ORDER_INPUTS_MUT = gql`
  mutation EditRecurringOrderInputs(
    $recurring_order_id: Int!
    $use_crops: [CreateOrderUseCropsInput!]!
    $use_blends: [CreateOrderUseBlendsInput!]!
    $should_create_overdue_orders: Boolean!
    $modify_in_progress_orders_all_inputs: Boolean!
  ) {
    editRecurringOrderInputs(
      input: {
        recurring_order_id: $recurring_order_id
        use_crops: $use_crops
        use_blends: $use_blends
        should_create_overdue_orders: $should_create_overdue_orders
        modify_in_progress_orders_all_inputs: $modify_in_progress_orders_all_inputs
      }
    ) {
      updated_recurring_order {
        id
        updated_at
      }
      updated_orders {
        id
        updated_at
      }
      updated_plantings {
        id
        planting_state
        trays_rounded
        order_id
        updated_at
      }
      updated_tasks {
        id
        order_id
        updated_at
      }
      deleted_tasks {
        id
      }
      deleted_plantings {
        id
      }
      deleted_inputs {
        id
      }
      deleted_orders {
        id
      }
      plantings_created {
        id
      }
      tasks_created {
        id
      }
      orders_created {
        id
      }
      inputs_created {
        id
      }
    }
  }
`;
const EDIT_REC_ORDER_INPUTS_DATA_QUERY = gql`
  query EditRecurringOrderInputsData($id: ID!, $todayStart: Int!) {
    surpluses(timeAfter: $todayStart) {
      edges {
        node {
          plantings {
            id
            trays_rounded
            planting_time
            crop {
              id
              crop_name
              yield_unit
              max_hold_time
            }
          }
        }
      }
    }
    recurring_order(id: $id) {
      id
      inputs {
        edges {
          node {
            id
            size_amount
            quantity
            unit
            price_per_item
            product_id
            crop {
              id
              crop_name
              status
              total_grow_time
              yield_per_tray
              yield_unit
              buffer_percentage
              harvest_buffer_time # needed for types
              edge_sizes {
                edges {
                  node {
                    id
                    size
                    unit
                    price_per_item
                  }
                }
              }
            }
            blend {
              id
              blend_name
              status
              edge_sizes {
                edges {
                  node {
                    id
                    size
                    unit
                    price_per_item
                  }
                }
              }
              crops {
                edges {
                  node {
                    id
                    percent_of_blend
                    crop {
                      id
                      total_grow_time
                      crop_name
                      yield_per_tray
                      yield_unit
                      buffer_percentage
                      harvest_buffer_time # needed for types
                    }
                  }
                }
              }
            }
          }
        }
      }
      plannedChildOrders: child_orders(
        filters: [
          { name: "order.order_status", operator: "=", value: "planned" }
        ]
        orderBy: [
          { name: "order.order_fulfillment_time", direction: "asc" }
        ]
      ) {
        edges {
          node {
            id
            order_fulfillment_time
            order_status
          }
        }
      }
      inProgressChildOrders: child_orders(
        filters: [
          { name: "order.order_status", operator: "=", value: "in_progress" }
        ]
        orderBy: [
          { name: "order.order_fulfillment_time", direction: "asc" }
        ]
      ) {
        edges {
          node {
            id
            order_fulfillment_time
            order_status
            plantings {
              edges {
                node {
                  id
                  planting_status
                  trays_rounded
                  yield_per_tray_ounces
                  crop {
                    id
                    crop_name
                    yield_unit
                    total_grow_time
                    harvest_buffer_time
                  }
                }
              }
            }
          }
        }
      }
    }
    crops(
      filters: [{ name: "crop.status", operator: "!=", value: "archived" }]
      orderBy: [{ name: "crop.crop_name", direction: "asc" }]
    ) {
      edges {
        node {
          id
          crop_name
          status
          total_grow_time
          harvest_buffer_time
          yield_per_tray
          yield_unit
          buffer_percentage
          edge_sizes {
            edges {
              node {
                id
                size
                unit
                price_per_item
              }
            }
          }
        }
      }
    }
    blends(
      filters: [{ name: "blend.status", operator: "!=", value: "archived" }]
      orderBy: [{ name: "blend.blend_name", direction: "asc" }]
    ) {
      edges {
        node {
          id
          blend_name
          status
          edge_sizes {
            edges {
              node {
                id
                size
                unit
                price_per_item
              }
            }
          }
          crops {
            edges {
              node {
                id
                percent_of_blend
                crop {
                  id
                  crop_name
                  total_grow_time
                  harvest_buffer_time
                  yield_per_tray
                  yield_unit
                  buffer_percentage
                }
              }
            }
          }
        }
      }
    }
  }
`;

interface Props {
  readonly recurringOrderId: number;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: (shouldRefetchSelf: boolean) => void;
}

export default function EditRecurringOrderInputsDialog({ recurringOrderId, isOpen, onClose, onSuccess }: Props) {
  const { timezone, preferredWeightUnit } = useUserContext();
  const { todayStart, todayStartDate, sevenDaysFromTodayStart } = getTimeRanges(timezone);
  const apolloClient = useApolloClient();
  const [getRecurringOrderData, { data: orderData, loading: orderDataLoading, error: orderDataError }] = useLazyQuery<
    EditRecurringOrderInputsDataQuery,
    EditRecurringOrderInputsDataQueryVariables
  >(EDIT_REC_ORDER_INPUTS_DATA_QUERY, {
    defaultOptions: {
      fetchPolicy: 'network-only',
    },
  });
  const [editRecurringOrderInputs, { loading: editRecurringOrderInputsLoading }] = useMutation<
    EditRecurringOrderInputsMutation,
    EditRecurringOrderInputsMutationVariables
  >(EDIT_REC_ORDER_INPUTS_MUT);

  const [filteredCropsData, setFilteredCropsData] = React.useState<CropData[]>([]);
  const [blendsData, setBlendsData] = React.useState<BlendData[]>([]);

  React.useEffect(() => {
    if (isOpen) {
      void getRecurringOrderData({
        variables: { id: createID.recurringOrder(recurringOrderId), todayStart },
        onCompleted: (orderDataIn) => {
          if (orderDataIn?.recurring_order != null && orderDataIn?.crops != null) {
            const readyFilteredCropsData = filterParseSortCropData(orderDataIn.crops);
            setFilteredCropsData(readyFilteredCropsData);

            const readyBlendsData = filterParseSortBlendData(orderDataIn.blends);
            setBlendsData(readyBlendsData);

            const inputs = orderDataIn.recurring_order.inputs.edges.map(({ node }) => node);
            const { cropFormValues, blendFormValues } = inputsToNewCropOrBlendToUseFormProps({
              orderInputs: inputs,
              preferredWeightUnit,
            });
            const cropOrBlendForms = cropFormValues.concat(blendFormValues).map((values, idx) => {
              const formKey = utils.getRandomInt();
              return (
                <NewCropOrBlendToUseForm
                  key={formKey}
                  formKey={formKey}
                  index={idx}
                  disableFields={disableFields}
                  values={values}
                  onChangeField={handleCropOrBlendFormInputChange}
                  onRemove={removeCropOrBlendForm}
                  cropsData={readyFilteredCropsData}
                  blendsData={readyBlendsData}
                />
              );
            });
            setCropOrBlendForms(cropOrBlendForms);
          }
        },
        onError: (errorIn) => {
          enqueueSnackbar(`Problem getting order data: ${errorIn.message}`, {
            variant: 'error',
            autoHideDuration: 10000,
          });
        },
      });
    }
  }, [isOpen, recurringOrderId, getRecurringOrderData, todayStart, preferredWeightUnit]);

  const disableFields = orderDataLoading || editRecurringOrderInputsLoading;

  const [cropOrBlendForms, setCropOrBlendForms] = React.useState<React.ReactElement<NewCropOrBlendToUseFormProps>[]>(
    [],
  );
  const [shouldCreateOverdueOrders, setShouldCreateOverdueOrders] = React.useState(true);
  const [modifyInProgressOrdersAllInputs, setModifyInProgressOrdersAllInputs] = React.useState(false);

  const onChangeModifyInProgressOrdersAllInputs = (e: React.ChangeEvent<HTMLInputElement>) => {
    setModifyInProgressOrdersAllInputs(e.target.checked);
  };
  const onChangeShouldCreateOverdueOrders = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShouldCreateOverdueOrders(e.target.checked);
  };

  const addCropOrBlendToUseForm = () => {
    setCropOrBlendForms((prevForms) => {
      const formKey = utils.getRandomInt();
      return [
        ...prevForms,
        <NewCropOrBlendToUseForm
          key={formKey}
          formKey={formKey}
          index={prevForms.length}
          disableFields={disableFields}
          values={{
            cropOrBlendSelected: undefined,
            itemCount: '',
            harvestTime: dayjs(),
            itemCountValidation: '',
            countUnit: createSizeKey({
              size: null,
              unit: preferredWeightUnit,
              pricePerItem: null,
              productId: null,
            }),
            pricePerItem: '',
            pricePerItemValidation: '',
          }}
          onChangeField={handleCropOrBlendFormInputChange}
          onRemove={removeCropOrBlendForm}
          cropsData={filteredCropsData}
          blendsData={blendsData}
          // onEnter={onEnter}
        />,
      ];
    });
  };
  const removeCropOrBlendForm = (index: number) => {
    setCropOrBlendForms((prevForms) => {
      const updatedForms = [...prevForms];
      updatedForms.splice(index, 1);
      return updatedForms.map((old, idx) => React.cloneElement(old, { index: idx }));
    });
  };
  const handleCropOrBlendFormInputChange = (
    index: number,
    fieldName: keyof CropOrBlendToUseFormInput,
    value: string | number | null | undefined | CropData | BlendData,
    validationFieldName?: string,
  ) => {
    setCropOrBlendForms((prevForms) => {
      const updatedForms = [...prevForms];
      // If fieldName is 'cropSelected', then check if the countUnit exists in the possible options
      // for the newly selected crop. If not then reset the countUnit to the default
      const existingFormValues: CropOrBlendToUseFormInput = updatedForms[index].props.values;
      let newCountUnit = existingFormValues.countUnit;
      if (fieldName === 'cropOrBlendSelected') {
        const newValue = value as CropData;
        const existingCountUnit = existingFormValues.countUnit;
        const possibleSizeKeys = newValue.sizes.map((size) =>
          createSizeKey({
            size: size.size,
            unit: size.unit,
            pricePerItem: size.price_per_item ?? null,
            productId: size.product_id ?? null,
          }),
        );
        if (!possibleSizeKeys.includes(existingCountUnit)) {
          newCountUnit = createSizeKey({
            size: null,
            unit: preferredWeightUnit,
            pricePerItem: null,
            productId: null,
          });
        }
      }
      const newValues: CropOrBlendToUseFormInput = {
        ...updatedForms[index].props.values,
        countUnit: newCountUnit,
        // This must come after countUnit is set
        // In case the countUnit is the value being changed
        [fieldName]: value,
      };
      if (validationFieldName !== undefined && value !== '') {
        // @ts-ignore
        newValues[validationFieldName] = '';
      }
      updatedForms[index] = React.cloneElement(updatedForms[index], {
        values: newValues,
      });

      return updatedForms;
    });
  };

  const onSubmit = () => {
    const { cropsToUse, blendsToUse } = preprareFormsForSubmit(cropOrBlendForms);
    void editRecurringOrderInputs({
      refetchQueries: [
        {
          query: DASHBOARD_QUERY,
          variables: {
            todayStart: todayStart.toString(),
            sevenDaysFromTodayStart: sevenDaysFromTodayStart.toString(),
          },
        },
        {
          query: DASHBOARD_OVERDUE_TASKS_QUERY,
          variables: {
            todayStart: todayStart.toString(),
            first: 25,
          },
        },
        {
          query: SURPLUSES_BUFFERS_QUERY,
          variables: {
            timeAfter: todayStart,
          },
        },
      ],
      update: (cache, { data }) => {
        const plantingsCreated = (data?.editRecurringOrderInputs?.plantings_created?.length ?? 0) > 0;
        const plantingsDeleted = (data?.editRecurringOrderInputs?.deleted_plantings?.length ?? 0) > 0;
        const tasksCreated = (data?.editRecurringOrderInputs?.tasks_created?.length ?? 0) > 0;
        const tasksDeleted = (data?.editRecurringOrderInputs?.deleted_tasks?.length ?? 0) > 0;
        const ordersCreated = (data?.editRecurringOrderInputs?.orders_created?.length ?? 0) > 0;
        const ordersDeleted = (data?.editRecurringOrderInputs?.deleted_orders?.length ?? 0) > 0;
        const inputsCreated = (data?.editRecurringOrderInputs?.inputs_created?.length ?? 0) > 0;
        const inputsDeleted = (data?.editRecurringOrderInputs?.deleted_inputs?.length ?? 0) > 0;
        cache.modify({
          fields: {
            harvests(existingGroup, { DELETE }) {
              return plantingsCreated || plantingsDeleted ? DELETE : existingGroup;
            },
            plantings(existingGroup, { DELETE }) {
              return plantingsCreated || plantingsDeleted ? DELETE : existingGroup;
            },
            task_groups(existingGroup, { DELETE }) {
              return tasksCreated || tasksDeleted ? DELETE : existingGroup;
            },
            tasks(existingTasks, { DELETE }) {
              return tasksCreated || tasksDeleted ? DELETE : existingTasks;
            },
            taskGroup(existingGroup, { DELETE }) {
              return tasksCreated ? DELETE : existingGroup;
            },
            plantingGroup(existingGroup, { DELETE }) {
              return plantingsCreated ? DELETE : existingGroup;
            },
            planting_groups(existingGroup, { DELETE }) {
              return plantingsCreated || plantingsDeleted ? DELETE : existingGroup;
            },
            plantingTimeGroup(existingGroup, { DELETE }) {
              return plantingsCreated ? DELETE : existingGroup;
            },
            orders(existingOrders, { DELETE }) {
              return ordersCreated || ordersDeleted ? DELETE : existingOrders;
            },
            order(existingOrder, { DELETE }) {
              return inputsCreated || inputsDeleted ? DELETE : existingOrder;
            },
          },
        });
      },
      variables: {
        recurring_order_id: recurringOrderId,
        use_crops: cropsToUse,
        use_blends: blendsToUse,
        should_create_overdue_orders: shouldCreateOverdueOrders,
        modify_in_progress_orders_all_inputs: modifyInProgressOrdersAllInputs,
      },
      onCompleted: (dataIn) => {
        if (dataIn !== undefined && dataIn.editRecurringOrderInputs != null) {
          const updatedOrderData = dataIn.editRecurringOrderInputs;
          enqueueSnackbar('Updated recurring order contents', { variant: 'success' });
          setCropOrBlendForms([]);
          evictCacheData({
            apolloClient,
            orderInputIds: updatedOrderData.deleted_inputs.map((i) => i.id),
            taskIds: updatedOrderData.deleted_tasks.map((t) => t.id),
            plantingIds: updatedOrderData.deleted_plantings.map((p) => p.id),
          });
          onSuccess(true);
        } else {
          enqueueSnackbar('Error editing order', { variant: 'error', autoHideDuration: 10000 });
        }
      },
      onError: (errorIn) => {
        enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
      },
    });
  };

  const hasInputValidationError = cropOrBlendForms.some(({ props }) => props.values.itemCountValidation !== '');
  const requiredFieldsFilled: boolean =
    cropOrBlendForms.length > 0 &&
    cropOrBlendForms.every(
      ({
        props: {
          values: { cropOrBlendSelected, itemCount },
        },
      }) => cropOrBlendSelected !== undefined && itemCount !== '' && Number(itemCount) !== 0,
    );
  const isSubmitDisabled: boolean = !requiredFieldsFilled || hasInputValidationError || disableFields;

  // const onEnter = (_event: React.KeyboardEvent<HTMLInputElement>) => {
  //   if (!isSubmitDisabled) {
  //     onSubmit();
  //   }
  // };

  const longestTotalGrowTimePlusBuffer = getLongestGrowTimePlusBufferFromInputForms(cropOrBlendForms);

  const inProgressChildOrders = orderData?.recurring_order?.inProgressChildOrders?.edges.map(({ node }) => node) ?? [];
  const plannedChildOrders = orderData?.recurring_order?.plannedChildOrders?.edges.map(({ node }) => node) ?? [];

  // If there are any planned orders that are now overdue because of the new longestTotalGrowTimePlusBuffer
  // Thow show those in a separate section
  // Update description based on checkbox state
  const plannedOrdersNowOverdue = plannedChildOrders
    .filter((order) => order.order_fulfillment_time - longestTotalGrowTimePlusBuffer < todayStart)
    .map((order) => ({
      ...order,
      extra: ` (new start date: ${formatTime(order.order_fulfillment_time - longestTotalGrowTimePlusBuffer, { timezone })})`,
    }));
  const plannedOrdersNotOverdue = plannedChildOrders.filter(
    (order) => order.order_fulfillment_time - longestTotalGrowTimePlusBuffer >= todayStart,
  );

  const traysFromForms = getTraysFromCropsAndBlends(cropOrBlendForms);
  const inProgressOrdersWithChanges = inProgressChildOrders.map((order) => {
    return {
      order,
      changes: getOrderChanges({
        traysFromForms,
        orderPlantingData: order.plantings.edges.map(({ node }) => node),
        surplusPlantings: orderData?.surpluses.edges.flatMap(({ node }) => node.plantings) ?? [],
        orderFulfillmentTime: dayjs.unix(order.order_fulfillment_time).tz(timezone),
        todayStartDate,
      }),
    };
  });

  const updateInformationContainer = (
    <Box>
      {inProgressChildOrders.length > 0 ? (
        modifyInProgressOrdersAllInputs ? (
          <ChildOrdersContainerWithChanges
            title={'The following in-progress child orders will be updated (see details):'}
            ordersIn={inProgressOrdersWithChanges}
          />
        ) : (
          <ChildOrdersContainer
            title={'The following in-progress child orders will not be affected:'}
            ordersIn={inProgressChildOrders}
          />
        )
      ) : null}
      {plannedOrdersNowOverdue.length > 0 && (
        <ChildOrdersContainer
          title={`The following planned child orders are now too late to start and will be ${
            shouldCreateOverdueOrders ? 're-created' : 'deleted'
          }:`}
          ordersIn={plannedOrdersNowOverdue}
        />
      )}
      {plannedOrdersNotOverdue.length > 0 && (
        <ChildOrdersContainer
          title="The following planned child orders will be re-created with the new inputs:"
          ordersIn={plannedOrdersNotOverdue}
        />
      )}
    </Box>
  );

  const form = (
    <Box display="flex" flexDirection="column" sx={{ m: 1 }} gap={1}>
      {cropOrBlendForms.map((cropOrBlendForm) => (
        <Box key={cropOrBlendForm.props.formKey}>{cropOrBlendForm}</Box>
      ))}
      <Box>
        <Grid container display="flex" flexDirection="row" alignItems="center">
          <Button
            variant="outlined"
            arial-label="add-crop-or-blend"
            onClick={addCropOrBlendToUseForm}
            disabled={editRecurringOrderInputsLoading}
            startIcon={<AddCircleOutlineIcon />}
          >
            Add Crop/Blend
          </Button>
          <Grid flexGrow="1" />
        </Grid>
      </Box>
      <Box>
        <Box display="flex" flexDirection="row" flexWrap="nowrap" alignItems="center" justifyContent="flex-start">
          <FormControlLabel
            label="Re-Create Overdue Orders"
            control={<Checkbox checked={shouldCreateOverdueOrders} onChange={onChangeShouldCreateOverdueOrders} />}
          />
          <Help tooltip="If changes are made to the contents that cause upcoming planned orders to now be overdue (too late to start all the crops), then checking this will make Microgreen Manager re-create those orders with the new inputs, even though you will now have overdue tasks." />
        </Box>
        <Box display="flex" flexDirection="row" flexWrap="nowrap" alignItems="center" justifyContent="flex-start">
          <FormControlLabel
            label="Modify In Progress Orders"
            control={
              <Checkbox checked={modifyInProgressOrdersAllInputs} onChange={onChangeModifyInProgressOrdersAllInputs} />
            }
          />
          <Help tooltip="By checking this, Microgreen Manager will modify the inputs for all in progress child orders. See the dropdown arrows for how each crop in the in progress orders will be affected. Note that any edits made to those child orders will be overriden by this change." />
        </Box>
      </Box>
      {updateInformationContainer}
    </Box>
  );

  const dialogContent = orderDataError ? (
    <ErrorView error={orderDataError} />
  ) : orderDataLoading || orderData?.recurring_order == null ? (
    <PageLoading />
  ) : (
    form
  );

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onClose();
      }}
      maxWidth="md"
    >
      <DialogTitle>
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Edit Recurring Order Contents</Typography>
          <ExitDialogButton dontShiftUp onCloseDialog={onClose} />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: 1 }}>{dialogContent}</DialogContent>
      <DialogActions disableSpacing sx={{ flexDirection: { md: 'row', xs: 'column' }, alignItems: 'center' }}>
        <Button sx={{ width: { xs: '100%', md: '50%' } }} variant="outlined" onClick={onClose} disabled={disableFields}>
          Cancel
        </Button>
        <Button
          id="submit-order-button"
          sx={{ width: { xs: '100%', md: '50%' }, ml: { md: 1 }, mt: { xs: 1, md: 0 } }}
          variant="contained"
          onClick={onSubmit}
          type="submit"
          disabled={isSubmitDisabled}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
